import React, { Dispatch } from 'react'
import { groupBy } from 'lodash-es'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { NegotiationStatus, NegotiationDetail, UserPreference } from 'api'
import {
  HeroPanel,
  BannerStatusTile,
  BannerCurrencyTile,
  BannerTilesGroup,
  DetailTilesContainer,
  EnvironmentTitle,
  getNegotiationCashAmounts
} from 'view/shared/heroPanel'

import { incomingNegotiationsCollectionNameSelector } from 'selectors/collectionSelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { WindowActions } from 'epics/windowEpic'
import { Translation, Locale } from 'locale'
import * as constants from 'view/styles/constants'
import { UnknownAction } from '@reduxjs/toolkit'

interface StateProps {
  collectionName: string | null
  userPreference: UserPreference
}
interface DispatchProps {
  onTearout: () => void
}
interface OwnProps {
  currentNegotiations: NegotiationDetail[]
  tearoutUrl?: string
  headerLabel?: string
}

export type IncomingNegotiationsHeroPanelProps = OwnProps
type IncomingNegotiationsHeroPanelComponentProps = OwnProps & DispatchProps & StateProps

const mapStateToProps = createStructuredSelector({
  collectionName: incomingNegotiationsCollectionNameSelector,
  userPreference: createUserPreferenceSelector()
})

const mapDispatchToProps = (dispatch: Dispatch<UnknownAction>, ownProps: IncomingNegotiationsHeroPanelProps) => ({
  onTearout: () => dispatch(WindowActions.tearoutGrid(ownProps.tearoutUrl ?? '/quote/tearout/rfqs/incoming'))
})

class IncomingNegotiationsHeroPanelComponent extends React.Component<IncomingNegotiationsHeroPanelComponentProps> {
  render() {
    const { currentNegotiations, collectionName, userPreference, headerLabel = Locale.translate(Translation.TC_HERO_INCOMING) } = this.props
    const negotiationsByStatus = groupBy(currentNegotiations, negotiationDetail => negotiationDetail.negotiation.status)
    const unpricedNegotiations = negotiationsByStatus[NegotiationStatus.REQUESTED_WITHOUT_PRICE] || []
    const pricedNegotiations = negotiationsByStatus[NegotiationStatus.REQUESTED_WITH_PRICE] || []
    const quotedNegotiations = negotiationsByStatus[NegotiationStatus.QUOTED] || []
    const cashAmounts = getNegotiationCashAmounts(currentNegotiations)
    const title = `${headerLabel} - ${collectionName || Locale.translate(Translation.TC_HERO_ALL)}`

    return (
      <HeroPanel onTearout={this.props.onTearout}>
        <DetailTilesContainer>
          <EnvironmentTitle data-test-id={'IncomingNegotiationsHeroPanel-EnvironmentTitle'}>{title}</EnvironmentTitle>
          <BannerTilesGroup title={Locale.translate(Translation.TC_HERO_STATUS)}>
            <BannerStatusTile
              title={Locale.translate(Translation.TC_UNPRICED)}
              label={unpricedNegotiations.length}
              statusColor={constants.STATUS_COLOR_DEFAULT}
            />
            <BannerStatusTile
              title={Locale.translate(Translation.TC_PRICED)}
              label={pricedNegotiations.length}
              statusColor={constants.STATUS_COLOR_ACTIVE}
            />
            <BannerStatusTile
              title={Locale.translate(Translation.TC_QUOTED)}
              label={quotedNegotiations.length}
              statusColor={constants.STATUS_COLOR_RESPOND}
            />
          </BannerTilesGroup>
        </DetailTilesContainer>
        {cashAmounts.length > 0 && (
          <DetailTilesContainer large={true}>
            <BannerTilesGroup title={Locale.translate(Translation.TC_HERO_CASH_AMOUNTS)}>
              {cashAmounts.map(cashAmount => (
                <BannerCurrencyTile key={cashAmount.currency} cashAmount={cashAmount} userPreference={userPreference} />
              ))}
            </BannerTilesGroup>
          </DetailTilesContainer>
        )}
      </HeroPanel>
    )
  }
}

export const IncomingNegotiationsHeroPanel = connect(mapStateToProps, mapDispatchToProps)(IncomingNegotiationsHeroPanelComponent)
